import { Component, computed, input, model } from "@angular/core";

import {
  CustomHeaderField,
  SEGY_COMMON_FIELDS,
  SegyFieldOption,
  SegyHeaderField,
  customHeaderFieldName,
} from "../../../../models/segy";

/** "9–12" byte-range label for a trace-header field. */
export function byteRangeLabel(
  bytes: [number, number] | null | undefined,
): string {
  if (!bytes) return "";
  return bytes[0] === bytes[1] ? `${bytes[0]}` : `${bytes[0]}–${bytes[1]}`;
}

/** Pseudo-field for plotting against the (1-based) trace number. */
export const TRACE_NUMBER_OPTION: SegyFieldOption = {
  value: "trace",
  label: "trace number",
  description: "1-based position of the trace in the file",
  group: "special",
};

/**
 * Build the picker options every header selector on the SEG-Y page shares:
 * the common fields first, then the user's custom byte/format fields, then
 * the remaining standard rev1 fields — each with its byte range and a one-line
 * description, so the same field reads the same way wherever it is chosen.
 */
export function buildFieldOptions(
  catalog: SegyHeaderField[],
  custom: CustomHeaderField[],
): SegyFieldOption[] {
  const common = new Set(SEGY_COMMON_FIELDS);
  const byName = new Map(catalog.map((f) => [f.name, f]));
  const standard = (f: SegyHeaderField, group: "common" | "standard") => ({
    value: f.name,
    label: f.name,
    bytes: f.bytes,
    description: f.scalar
      ? `${f.description} (× ${f.scalar} in real units)`
      : f.description,
    group,
  });
  return [
    ...SEGY_COMMON_FIELDS.filter((n) => byName.has(n)).map((n) =>
      standard(byName.get(n)!, "common"),
    ),
    ...custom.map((f) => {
      const width = { int8: 1, uint8: 1, int16: 2, uint16: 2 }[f.format] ?? 4;
      return {
        value: customHeaderFieldName(f),
        label: `byte ${f.byte} · ${f.format}`,
        bytes: [f.byte, f.byte + width - 1] as [number, number],
        description: "custom field",
        group: "custom" as const,
      };
    }),
    ...catalog
      .filter((f) => !common.has(f.name))
      .map((f) => standard(f, "standard")),
  ];
}

const GROUP_TITLES: Record<SegyFieldOption["group"], string> = {
  special: "",
  common: "Common",
  custom: "Custom fields",
  standard: "All other standard fields",
};

/**
 * The one header-field picker used across the SEG-Y viewer (gather grouping,
 * header plot axes, geometry colouring, table columns). Searchable by name,
 * byte or description; options show their byte range and meaning.
 */
@Component({
  selector: "app-segy-field-select",
  templateUrl: "./field-select.component.html",
  styleUrl: "./field-select.component.less",
  standalone: false,
})
export class SegyFieldSelectComponent {
  public readonly options = input<SegyFieldOption[]>([]);
  /** prepended options such as {@link TRACE_NUMBER_OPTION} */
  public readonly extraOptions = input<SegyFieldOption[]>([]);
  public readonly value = model<string | string[] | null>(null);
  public readonly multiple = input<boolean>(false);
  public readonly allowClear = input<boolean>(false);
  public readonly placeholder = input<string>("header field");
  public readonly maxTagCount = input<number>(8);

  protected readonly groups = computed(() => {
    const all = [...this.extraOptions(), ...this.options()];
    return (["special", "common", "custom", "standard"] as const)
      .map((group) => ({
        title: GROUP_TITLES[group],
        options: all.filter((o) => o.group === group),
      }))
      .filter((g) => g.options.length);
  });

  private readonly searchIndex = computed(() => {
    const index = new Map<string, string>();
    for (const o of [...this.extraOptions(), ...this.options()]) {
      index.set(
        o.value,
        `${o.label} ${o.value} ${byteRangeLabel(o.bytes)} ${o.description ?? ""}`.toLowerCase(),
      );
    }
    return index;
  });

  /** nz-select filter: match name, byte position or description. */
  protected readonly filterOption = (input: string, option: any): boolean =>
    (this.searchIndex().get(option.nzValue) ?? "").includes(
      input.toLowerCase(),
    );

  protected readonly byteLabel = byteRangeLabel;
}
